import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCountdown } from "@/lib/useCountdown";

// All reward cards use the wallet quote, including its amount, eligibility and cooldown.
export function useDailyReward() {
  const { refresh } = useAuth();
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const [bonus, setBonus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const mounted = useRef(false);
  const requestVersion = useRef(0);
  const claimingRef = useRef(false);
  const bonusRef = useRef(null);

  const reload = useCallback(async () => {
    const version = ++requestVersion.current;
    try {
      const { data } = await api.get("/wallet");
      if (!data?.bonus || typeof data.bonus.can_claim !== "boolean" || data.bonus.amount == null) {
        throw new Error("Daily reward unavailable");
      }
      if (mounted.current && version === requestVersion.current) {
        bonusRef.current = data.bonus;
        setBonus(data.bonus);
        setError(false);
      }
    } catch {
      if (mounted.current && version === requestVersion.current) setError(true);
    } finally {
      if (mounted.current && version === requestVersion.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    reload();
    window.addEventListener("focus", reload);
    return () => {
      mounted.current = false;
      requestVersion.current += 1;
      window.removeEventListener("focus", reload);
    };
  }, [reload]);

  const { text: countdown } = useCountdown(bonus?.can_claim === false ? bonus.next_claim_at : null, reload);
  const claim = useCallback(async () => {
    if (claimingRef.current || !bonusRef.current?.can_claim) return null;
    claimingRef.current = true;
    setClaiming(true);
    try {
      const { data } = await api.post("/wallet/claim-bonus");
      // Keep the successful cooldown even if the subsequent wallet refresh fails.
      requestVersion.current += 1;
      bonusRef.current = { ...bonusRef.current, can_claim: false, next_claim_at: data.next_claim_at };
      if (mounted.current) setBonus(bonusRef.current);
      await Promise.allSettled([Promise.resolve().then(() => refreshRef.current?.()), reload()]);
      return data;
    } catch (err) {
      await reload();
      throw err;
    } finally {
      claimingRef.current = false;
      if (mounted.current) setClaiming(false);
    }
  }, [reload]);

  return {
    bonus, loading, error, claiming, claim, reload, countdown,
    canClaim: bonus?.can_claim === true && !error,
    onCooldown: bonus?.can_claim === false && !error,
  };
}
